import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MediaItem } from '@/types/posts';
import { PostVariation, CreateVariationData, UpdateVariationData } from '@/types/variations';
import { Post, PostMedia } from '@/services/posts';
import { PlatformOptions } from '@/types/scheduled-instances';
import { CreateMappingData } from '@/types/calendar-mappings';
import { variationsService } from '@/services/variations.service';
import { calendarMappingsService } from '@/services/calendar-mappings.service';
import { scheduledInstancesService } from '@/services/scheduled-instances.service';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useEffectiveTeamId } from './useEffectiveTeamId';
import { useChannels } from './useChannels';
import { invalidateReleaseState } from '@/lib/invalidateRelease';
import { toast } from '@/utils/toast';
import { approvalService } from '@/services/approval.service';
import { announceReleaseMoment } from '@/stores/releaseMoment.store';
import { announceRelease } from '@/stores/releaseUndo.store';
import { getReleaseFailureReason } from '@/utils/releaseFailureReason';
import type { ReleaseResult } from '@/types/approval';
import { SUPPORTED_LANGUAGES } from '@/utils/languages';
import { languageFromTitle } from '@/utils/languageFromTitle';
import apiClient, { formatApiError } from '@/services/api';
import {
  shootWithRecovery,
  buildAlreadyScheduledMessage,
  classifySubmitWarning,
  type ShootOutcome,
} from '@/utils/shootRecovery';
import {
  checkCaptionWithinLimits,
  getYoutubeEffectiveCaptionText,
  CAPTION_TOO_LONG_ERROR_CODE,
} from '@/utils/captionLimit';

/**
 * Extract error message from various error types (Axios, Error, etc.)
 */
function extractErrorMessage(error: unknown, fallback: string): string {
  // Handle Axios errors with API response
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosErr = error as { response?: { data?: { error?: string; message?: string } } };
    const apiError = axiosErr.response?.data?.error || axiosErr.response?.data?.message;
    if (apiError) return apiError;
  }
  // Handle standard Error objects
  if (error instanceof Error) return error.message;
  // Fallback
  return fallback;
}

/**
 * Human-friendly fallback for the backend's caption-too-long rejection, used only
 * if the descriptive message were ever missing (defensive — the backend currently
 * always includes one, see post.service.ts / scheduled-instance.service.ts).
 */
const CAPTION_TOO_LONG_FALLBACK =
  'Your caption is too long for one of the selected platforms. Please shorten it and try again.';

/**
 * If the API error is the backend's caption-too-long rejection (AppError code
 * 'caption_too_long'), return a clean, user-facing message. Returns null for any
 * other error so callers can fall through to their normal error extraction.
 */
function getCaptionTooLongMessage(error: unknown): string | null {
  const apiError = formatApiError(error);
  if (apiError.code !== CAPTION_TOO_LONG_ERROR_CODE) return null;
  return apiError.message || CAPTION_TOO_LONG_FALLBACK;
}

/**
 * useVariationComposer Hook
 *
 * Local-state React hook for variation composition that mirrors usePostComposer's API
 * but uses local useState (NOT Zustand composerStore) to avoid conflicts when both
 * PostCreator and VariationCreator are mounted simultaneously.
 *
 * Implements "save first, then shoot" principle:
 * - Step 1: Save variation (create or update)
 * - Step 2: If rocket ON: Create calendar mapping, then shoot
 *
 * Usage:
 * ```tsx
 * const composer = useVariationComposer({
 *   variation: existingVariation, // For edit mode
 *   parentPost: post,
 *   onSuccess: () => closeModal(),
 * });
 *
 * // Primary action (rocket button)
 * await composer.handlePrimaryAction({
 *   rocketOn: true,
 *   shootMode: 'shoot_now',
 *   channelIds: [1, 2, 3],
 *   scheduledTime: new Date(),
 *   channelOptions: {}
 * });
 * ```
 */

export interface UseVariationComposerOptions {
  /**
   * Callback on successful save/shoot
   */
  onSuccess?: () => void;

  /**
   * Callback on error
   */
  onError?: (error: Error) => void;

  /**
   * Existing variation for edit mode (optional)
   */
  variation?: PostVariation;

  /**
   * Parent post (loaded separately)
   */
  parentPost?: Post | null;

  /**
   * Calendar mapping ID for shooting mode
   */
  shootingMappingId?: number;
}

export interface VariationPrimaryActionParams {
  rocketOn: boolean;
  shootMode: 'shoot_now' | 'schedule';
  channelIds: number[];
  scheduledTime: Date | null;
  channelOptions: Record<number, PlatformOptions>;
}

/**
 * Return type of useVariationComposer hook
 * Use this to properly type the composer prop in child components
 */
/** Snapshot the dirty check compares the live form against. */
interface ComposerBaseline {
  title: string;
  language: string | null;
  caption: string;
  media: MediaItem[];
  labels: string[];
  mediaMode: 'inherit' | 'custom';
  selectedChannels: number[];
  scheduledTime: Date | null;
  channelOptions: Record<number, PlatformOptions>;
}

/** Baseline of a blank form (create mode, and after a discard/reset). */
const createBlankBaseline = (): ComposerBaseline => ({
  title: '',
  language: null,
  caption: '',
  media: [],
  labels: [],
  mediaMode: 'inherit',
  selectedChannels: [],
  scheduledTime: null,
  channelOptions: {},
});

export type UseVariationComposerReturn = ReturnType<typeof useVariationComposer>;

export function useVariationComposer(options: UseVariationComposerOptions = {}) {
  const { onSuccess: onSuccessCallback, onError, variation, parentPost, shootingMappingId } = options;

  // Query client for cache invalidation
  const queryClient = useQueryClient();
  const { formatDateTime } = useTimezone();

  // Get effective team ID for team-scoped queries
  const { teamId } = useEffectiveTeamId();

  // Channels list — used to resolve selected channel IDs to platform keys for the
  // pre-submit caption length re-validation below. Shares the react-query cache
  // with VariationCreator's own useChannels() call, so this is not an extra request.
  const { data: channels } = useChannels();

  const { t, i18n } = useTranslation('posts');

  // Determine edit mode
  const isEditMode = !!variation;

  // Parent post ID (from variation or parentPost)
  const parentPostId = useMemo(
    () => parentPost?.id ?? variation?.postId ?? null,
    [parentPost?.id, variation?.postId]
  );

  // Content state (local - NOT Zustand)
  const [title, setTitle] = useState('');
  // Explicit language of the version (lowercase code, null = none). Independent of the title: renaming
  // the title never changes it. Only the user's own pick, or the pre-fill below, sets it.
  const [language, setLanguageState] = useState<string | null>(null);
  const languageTouchedRef = useRef(false);
  const [caption, setCaption] = useState('');
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [mediaMode, setMediaMode] = useState<'inherit' | 'custom'>('inherit');

  // Channels/Schedule state (for shooting)
  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
  const [channelOptions, setChannelOptions] = useState<Record<number, PlatformOptions>>({});
  const [isCarouselMode, setCarouselMode] = useState(false);

  // Calendar state (for mapping)
  const [calendarId, setCalendarId] = useState<number | null>(null);
  const [calendarNoteId, setCalendarNoteId] = useState<number | null>(null);
  const [calendarHolidayId, setCalendarHolidayId] = useState<number | null>(null);

  // Changed thumbnails (for custom thumbnails)
  const [changedThumbnails, setChangedThumbnails] = useState<Map<string, string>>(new Map());

  // Dirty tracking (for discard confirmation)
  const [initialState, setInitialState] = useState<ComposerBaseline>(createBlankBaseline);

  const isDirty = useMemo(() => {
    return (
      title !== initialState.title ||
      language !== initialState.language ||
      caption !== initialState.caption ||
      media.length !== initialState.media.length ||
      media.some((item, index) => {
        const initial = initialState.media[index];
        return !initial || item.id !== initial.id || item.url !== initial.url ||
          item.thumbnail !== initial.thumbnail || item.type !== initial.type;
      }) ||
      labels.length !== initialState.labels.length ||
      labels.some((label, index) => label !== initialState.labels[index]) ||
      mediaMode !== initialState.mediaMode ||
      selectedChannels.length !== initialState.selectedChannels.length ||
      selectedChannels.some((id, i) => id !== initialState.selectedChannels[i]) ||
      scheduledTime?.getTime() !== initialState.scheduledTime?.getTime() ||
      JSON.stringify(channelOptions) !== JSON.stringify(initialState.channelOptions) ||
      changedThumbnails.size > 0
    );
  }, [title, language, caption, media, labels, mediaMode, selectedChannels, scheduledTime, channelOptions, changedThumbnails, initialState]);

  // The CONTENT of the version (not the schedule fields) differs from what was loaded.
  const isContentDirty =
    title !== initialState.title ||
    language !== initialState.language ||
    caption !== initialState.caption ||
    mediaMode !== initialState.mediaMode ||
    media.length !== initialState.media.length ||
    media.some((item, index) => {
      const initial = initialState.media[index];
      return !initial || item.id !== initial.id || item.url !== initial.url ||
        item.thumbnail !== initial.thumbnail || item.type !== initial.type;
    }) ||
    labels.length !== initialState.labels.length ||
    labels.some((label, index) => label !== initialState.labels[index]) ||
    changedThumbnails.size > 0;

  /**
   * Load variation data for edit mode
   */
  const loadVariation = (variation: PostVariation, parentPost: Post) => {
    createdVariationRef.current = null;
    try {
      const loadedTitle = variation.title;
      const loadedCaption = variation.caption || '';
      const loadedLabels = variation.labels || [];

      const loadedLanguage = variation.language ?? null;
      setTitle(loadedTitle);
      setLanguageState(loadedLanguage);
      languageTouchedRef.current = true; // an existing version keeps its stored language
      setCaption(loadedCaption);
      setLabels(loadedLabels);

      // Determine media mode
      const parentMediaIds = parentPost.postMedia?.map((m: PostMedia) => m.id).sort().join(',') ?? '';
      const variationMediaIds =
        variation.postMedia?.map((m) => m.id.toString()).sort().join(',') ?? '';

      let loadedMediaMode: 'inherit' | 'custom' = 'inherit';
      let loadedMedia: MediaItem[] = [];

      if (variationMediaIds && variationMediaIds !== parentMediaIds) {
        loadedMediaMode = 'custom';
        loadedMedia = variation.postMedia?.map((m) => ({
          id: m.id.toString(),
          url: m.originalUrl ?? '',
          type: m.type.toLowerCase() as 'image' | 'video' | 'pdf',
          thumbnail: m.thumbnailUrl || undefined,
          width: m.width,
          height: m.height,
          size: m.size,
        })) || [];
        setMediaMode('custom');
        setMedia(loadedMedia);
      } else {
        setMediaMode('inherit');
        setMedia([]); // Will use parent media
      }

      // Update initial state for dirty tracking
      setInitialState({
        title: loadedTitle,
        language: loadedLanguage,
        caption: loadedCaption,
        media: loadedMedia,
        labels: loadedLabels,
        mediaMode: loadedMediaMode,
        selectedChannels: [],
        scheduledTime: null,
        channelOptions: {},
      });
    } catch (error) {
      console.error('[VariationComposer] Failed to load variation:', error);
    }
  };

  /**
   * Save variation (create or update)
   * Returns the saved variation or null on error
   */
  // Id of the version this composer created (create mode); the next save updates it instead of creating another.
  // Cleared whenever the composer finishes (success), loads another version, or is discarded.
  const createdVariationRef = useRef<{ parentPostId: number; id: number } | null>(null);
  // Keyed by its parent: picking a different post (step 1) must never reuse the old version id.
  useEffect(() => {
    createdVariationRef.current = null;
  }, [parentPostId]);
  const onSuccess = (): void => {
    createdVariationRef.current = null;
    onSuccessCallback?.();
  };

  const saveMutation = useMutation({
    mutationFn: async (): Promise<PostVariation | null> => {
      if (!parentPostId) {
        throw new Error('Parent post ID is required');
      }

      if (!title.trim()) {
        throw new Error('Title is required');
      }

      // A version this composer already created is adopted: a retry (e.g. after a failed send for review)
      // is an UPDATE of it, never a second create.
      const updateTargetId: number | null = isEditMode && variation
          ? variation.id
          : createdVariationRef.current?.parentPostId === parentPostId
            ? createdVariationRef.current.id
            : null;
      if (updateTargetId !== null) {
        // Convert changedThumbnails Map to object for API
        const mediaThumbnails =
          changedThumbnails.size > 0 ? Object.fromEntries(changedThumbnails) : undefined;

        // Update existing variation
        const updateData: UpdateVariationData = {
          title: title.trim(),
          caption: caption.trim() || undefined,
          labels: labels.length > 0 ? labels : undefined,
          mediaThumbnails,
          // Only a changed language is sent (null = clear), so a plain save never touches it
          ...(language !== initialState.language ? { language } : {}),
        };
        const updated = await variationsService.update(
          parentPostId,
          updateTargetId,
          updateData
        );

        // Update media only if it actually changed
        const mediaChanged = mediaMode !== initialState.mediaMode ||
          media.length !== initialState.media.length ||
          media.some((m, i) => m.id !== initialState.media[i]?.id);

        if (mediaChanged) {
          if (mediaMode === 'custom' && media.length > 0) {
            // Keep IDs as strings - backend handles both UUIDs and numeric IDs
            const mediaIds = media.map((m) => m.id);
            await variationsService.updateVariationMedia(parentPostId, updateTargetId, { mediaIds });
          } else if (mediaMode === 'inherit') {
            // User switched from custom to inherit — clear variation media
            await variationsService.updateVariationMedia(parentPostId, updateTargetId, { mediaIds: [] });
          }
        }

        return updated;
      } else {
        // Convert changedThumbnails Map to object for API
        const mediaThumbnails =
          changedThumbnails.size > 0 ? Object.fromEntries(changedThumbnails) : undefined;

        // Create new variation
        // Keep IDs as strings - backend handles both UUIDs and numeric IDs
        const createData: CreateVariationData = {
          title: title.trim(),
          caption: caption.trim() || undefined,
          labels: labels.length > 0 ? labels : undefined,
          mediaIds: mediaMode === 'custom' ? media.map((m) => m.id) : undefined,
          mediaThumbnails,
          language,
        };
        const created = await variationsService.create(parentPostId, createData);
        if (created) createdVariationRef.current = { parentPostId, id: created.id };
        return created;
      }
    },
    onSuccess: () => {
      invalidateReleaseState(queryClient, { teamId, postId: parentPostId ?? undefined });
    },
  });

  /**
   * Shoot mutation (for creating scheduled instances)
   */
  const shootMutation = useMutation({
    mutationFn: async (data: {
      calendarMappingId: number;
      scheduledAt: Date;
      channelIds: number[];
      channelOptions?: Record<number, PlatformOptions>;
      immediate?: boolean;
    }) => {
      return scheduledInstancesService.shoot(data);
    },
  });

  /**
   * Save variation only (no shooting)
   */
  const handleSave = async (): Promise<PostVariation | null> => {
    try {
      const savedVariation = await saveMutation.mutateAsync();

      if (savedVariation) {
        // Invalidate queries
        invalidateReleaseState(queryClient, { teamId, postId: parentPostId ?? undefined });

        toast.success(isEditMode ? 'Variation updated' : 'Variation created');
        onSuccess();
        return savedVariation;
      }

      return null;
    } catch (error) {
      const submitWarning = classifySubmitWarning(error, t, formatDateTime);
      if (submitWarning) {
        toast.warning(submitWarning.message);
        onError?.(new Error(submitWarning.message));
        return null;
      }
      const message = extractErrorMessage(error, 'Failed to save variation');
      toast.error(message);
      onError?.(error instanceof Error ? error : new Error(message));
      return null;
    }
  };

  /**
   * Save and mark ready (for approval workflow)
   */
  const handleSaveAndMarkReady = async (): Promise<void> => {
    try {
      if (!parentPostId) {
        throw new Error('Parent post ID is required');
      }

      // Step 1: Save the variation
      const savedVariation = await saveMutation.mutateAsync();

      if (!savedVariation) {
        throw new Error('Failed to save variation');
      }

      // Step 2: Mark ready (update contentStatus to READY)
      await apiClient.patch(
        `/api/posts/${parentPostId}/variations/${savedVariation.id}`,
        { contentStatus: 'READY' }
      );

      // Invalidate queries
      invalidateReleaseState(queryClient, { teamId, postId: parentPostId ?? undefined });

      toast.success('Variation saved and marked ready');
      onSuccess();
    } catch (error) {
      const submitWarning = classifySubmitWarning(error, t, formatDateTime);
      if (submitWarning) {
        toast.warning(submitWarning.message);
        onError?.(new Error(submitWarning.message));
        return;
      }
      const message = extractErrorMessage(error, 'Failed to save and mark ready');
      toast.error(message);
      onError?.(error instanceof Error ? error : new Error(message));
    }
  };

  /**
   * Save the variation (create or update, the existing path), then act on the saved version: send it for
   * review, or release it directly. The content is stored FIRST, so the reviewer / the release snapshot sees
   * the fixed version. Gated in the UI by the server verdicts (`canSendForReview`, `canReleaseDirect`).
   * A second trigger while one runs is ignored with the "Still saving…" toast (single save lock).
   */
  const saveThenActLockRef = useRef(false);

  const runSaveThenAct = async (action: 'review' | 'release'): Promise<void> => {
    if (saveThenActLockRef.current) {
      toast.info(t('common:saving_still_saving'));
      return;
    }
    saveThenActLockRef.current = true;
    try {
      if (!parentPostId) {
        throw new Error('Parent post ID is required');
      }
      const savedVariation = await saveMutation.mutateAsync();
      if (!savedVariation) {
        throw new Error('Failed to save variation');
      }

      if (action === 'review') {
        await approvalService.submitVariationForApproval(parentPostId, savedVariation.id);
        invalidateReleaseState(queryClient, { teamId, postId: parentPostId });
        announceReleaseMoment('sent');
        onSuccess();
        return;
      }

      // Release. The variation IS saved at this point: a failure here is "Saved as draft", never "Released".
      let result: ReleaseResult;
      try {
        result = await approvalService.releaseVariationDirect(parentPostId, savedVariation.id);
      } catch (releaseError) {
        invalidateReleaseState(queryClient, { teamId, postId: parentPostId });
        toast.warning(
          t('common:composer_saved_not_released', { reason: getReleaseFailureReason(releaseError, i18n) })
        );
        onSuccess();
        return;
      }
      invalidateReleaseState(queryClient, { teamId, postId: parentPostId });
      announceRelease({
        postId: parentPostId,
        variationId: savedVariation.id,
        undoUntil: result.undoUntil,
        reviewedAt: result.reviewedAt,
        undoAvailable: result.undoAvailable,
      });
      announceReleaseMoment('released', 1, result.undoAvailable === false);
      onSuccess();
    } catch (error) {
      const submitWarning = classifySubmitWarning(error, t, formatDateTime);
      if (submitWarning) {
        toast.warning(submitWarning.message);
        onError?.(new Error(submitWarning.message));
        return;
      }
      const message = extractErrorMessage(
        error,
        action === 'release' ? 'Failed to save and release' : 'Failed to send for review'
      );
      toast.error(message);
      // The save may have gone through: refresh so the composer shows the stored state
      invalidateReleaseState(queryClient, { teamId, postId: parentPostId ?? undefined });
      onError?.(error instanceof Error ? error : new Error(message));
    } finally {
      saveThenActLockRef.current = false;
    }
  };

  const handleSaveAndSendForReview = (): Promise<void> => runSaveThenAct('review');
  const handleSaveAndRelease = (): Promise<void> => runSaveThenAct('release');

  /**
   * Resolve channel IDs to their lowercased platform keys (e.g. 'linkedin',
   * 'google_my_business'), for platform-limit checks. Mirrors the
   * selectedPlatforms() helper in VariationCreator/index.tsx exactly (same
   * fallback to 'linkedin' when no channels are resolvable) so the pre-submit
   * caption check below evaluates the same platform set the disabled-button
   * guard did.
   */
  const getPlatformsForChannelIds = (channelIds: number[]): string[] => {
    if (channelIds.length === 0) return ['linkedin'];
    const activeChannels = channels?.filter((ch) => channelIds.includes(ch.id));
    if (!activeChannels || activeChannels.length === 0) return ['linkedin'];
    return [...new Set(activeChannels.map((ch) => ch.platform.toLowerCase()))];
  };

  /**
   * Primary Action Handler - Unified save + optional shoot
   *
   * Implements "save first, then shoot" principle:
   * - Step 1: Save variation (create or update)
   * - Step 2: If rocketOn: Create calendar mapping with variationId, then shoot
   */
  const handlePrimaryAction = async (params: VariationPrimaryActionParams) => {
    const { rocketOn, shootMode, channelIds, scheduledTime: shootTime, channelOptions } = params;

    // Belt-and-suspenders re-validation: the rocket/publish CTA is already disabled
    // while the caption exceeds a selected platform's limit (see getDisabledReason()
    // in VariationCreator), but that's a UI guard only. Re-run the identical check
    // here so a bypassed/disabled button, race, or keyboard shortcut can never
    // submit an over-limit caption to the API. Only applies when actually
    // scheduling/shooting (rocketOn) — saves are unrestricted, matching both the
    // UI guard and the backend (which only validates caption length when
    // scheduledAt/shoot is involved).
    if (rocketOn && caption.trim()) {
      const platforms = getPlatformsForChannelIds(channelIds);
      // YouTube publishes its per-channel description override (if set) instead of the
      // caption — validate that, not the caption, so a long caption + short YouTube
      // description isn't wrongly blocked (and vice versa).
      const youtubeText = getYoutubeEffectiveCaptionText(caption, channelIds, channelOptions, channels);
      const captionCheck = checkCaptionWithinLimits(caption, platforms, { youtube: youtubeText });
      if (!captionCheck.isWithinAll) {
        const message = t('caption_over_limit', {
          defaultValue: 'Caption too long for: {{platforms}}',
          platforms: captionCheck.overPlatformNames,
        });
        toast.error(message);
        onError?.(new Error(message));
        return;
      }
    }

    let shootOutcome: ShootOutcome = 'shot';
    let liveScheduledAt: string | null = null;
    // Mapping THIS attempt created (never a pre-existing one): removed again when the shoot does not happen.
    let createdMappingId: number | null = null;
    const removeCreatedMapping = async (): Promise<void> => {
      if (createdMappingId === null) return;
      const id = createdMappingId;
      createdMappingId = null;
      try {
        // The server only deletes a mapping without live instances, so a shoot that actually went through is safe.
        await calendarMappingsService.deleteMapping(id);
      } catch (cleanupError) {
        console.warn('[VariationComposer] Could not remove the unused calendar mapping', id, cleanupError);
      }
    };
    try {
      if (!parentPostId) {
        throw new Error('Parent post ID is required');
      }

      if (!calendarId) {
        throw new Error('Calendar is required for shooting');
      }

      // STEP 1: SAVE. Scheduling an existing version whose content did not change (typically a RELEASED,
      // content-frozen one: the rocket flow) must NOT send a content save, only the scheduling calls below.
      const savedVariation =
        rocketOn && isEditMode && variation && !isContentDirty ? variation : await saveMutation.mutateAsync();

      if (!savedVariation) {
        throw new Error('Failed to save variation');
      }

      // STEP 2: SHOOT (if rocket ON)
      if (rocketOn) {
        let mappingIdToShoot: number;

        // If shooting mode (existing mapping), use that mapping instead of creating a new one
        if (shootingMappingId) {
          mappingIdToShoot = shootingMappingId;
        } else {
          // Create new calendar mapping with variationId
          const mappingData: CreateMappingData = {
            postId: parentPostId,
            variationId: savedVariation.id,
            calendarId,
            plannedDate: shootTime || new Date(),
            calendarNoteId: calendarNoteId || undefined,
            calendarHolidayId: calendarHolidayId || undefined,
          };

          const mapping = await calendarMappingsService.createMapping(mappingData);
          mappingIdToShoot = mapping.id;
          createdMappingId = mapping.id;
        }

        // Shoot the mapping
        const isImmediate = shootMode === 'shoot_now';
        const finalShootTime = isImmediate ? new Date() : (shootTime || new Date());

        const shootResult = await shootWithRecovery(
          {
            calendarMappingId: mappingIdToShoot,
            scheduledAt: finalShootTime,
            channelIds,
            channelOptions: Object.keys(channelOptions).length > 0 ? channelOptions : undefined,
            immediate: isImmediate,
          },
          shootMutation.mutateAsync
        );
        shootOutcome = shootResult.outcome;
        liveScheduledAt = shootResult.liveScheduledAt;
        // Same day + channel already scheduled: nothing new was shot, so the mapping created above is unused.
        if (shootOutcome === 'already_scheduled') await removeCreatedMapping();
      }

      // STEP 3: Clean up and invalidate
      invalidateReleaseState(queryClient, { teamId, postId: parentPostId ?? undefined });

      const successMsg = rocketOn
        ? shootMode === 'shoot_now'
          ? 'Variation scheduled for immediate publishing!'
          : 'Variation scheduled successfully!'
        : 'Variation saved!';

      if (shootOutcome === 'already_scheduled') {
        toast.info(buildAlreadyScheduledMessage(liveScheduledAt, formatDateTime));
      } else {
        toast.success(successMsg);
      }
      onSuccess();
    } catch (error) {
      await removeCreatedMapping();
      // Unconfirmed shoot / same-day already_scheduled 409: amber warning, NOT a red error.
      // Nothing is reset, so the user keeps their draft, time and channels.
      const submitWarning = classifySubmitWarning(error, t, formatDateTime);
      if (submitWarning) {
        // Clear the failed shoot mutation so the composer shows no red error banner (amber toast only).
        shootMutation.reset();
        toast.warning(submitWarning.message);
        if (submitWarning.kind === 'conflict') onError?.(new Error(submitWarning.message));
        return;
      }
      // Surface the backend's caption-too-long rejection clearly (e.g. a race that
      // slipped past the guard above, or a caption edited after the guard ran but
      // before the request completed). Falls back to normal error extraction for
      // every other error.
      // Scheduling hit the content freeze (released / in review): say what to do instead of the generic frozen text.
      const frozenWhileScheduling = rocketOn && (formatApiError(error).code ?? '').startsWith('release.frozen');
      const message = frozenWhileScheduling
        ? t('common:release_schedule_frozen_hint')
        : getCaptionTooLongMessage(error) || extractErrorMessage(error, 'Failed to process variation');
      toast.error(message);
      onError?.(error instanceof Error ? error : new Error(message));
    }
  };

  /**
   * Discard changes and reset
   */
  const handleDiscard = () => {
    createdVariationRef.current = null;
    setTitle('');
    setLanguageState(null);
    languageTouchedRef.current = false;
    setCaption('');
    setMedia([]);
    setLabels([]);
    setMediaMode('inherit');
    setSelectedChannels([]);
    setScheduledTime(null);
    setChannelOptions({});
    setCarouselMode(false);
    setCalendarId(null);
    setCalendarNoteId(null);
    setCalendarHolidayId(null);
    setChangedThumbnails(new Map());
    // The baseline goes back to blank together with the live state: platform defaults merged into it earlier
    // (updateChannelOptions asDefault) must not make the next blank form read dirty.
    setInitialState(createBlankBaseline());
  };

  /**
   * Full reset (same as handleDiscard)
   */
  const reset = () => {
    handleDiscard();
    saveMutation.reset();
    shootMutation.reset();
  };

  /**
   * Media helpers
   */
  const addMedia = (item: MediaItem) => {
    setMedia((prev) => [...prev, item]);
  };

  const removeMedia = (id: string) => {
    setMedia((prev) => prev.filter((m) => m.id !== id));
  };

  const reorderMedia = (fromIndex: number, toIndex: number) => {
    setMedia((prev) => {
      const newMedia = [...prev];
      const [removed] = newMedia.splice(fromIndex, 1);
      newMedia.splice(toIndex, 0, removed);
      return newMedia;
    });
  };

  const setChangedThumbnail = (mediaId: string, thumbnailUrl: string) => {
    setChangedThumbnails((prev) => {
      const next = new Map(prev);
      next.set(mediaId, thumbnailUrl);
      return next;
    });
  };

  /**
   * Update channel-specific options
   */
  const updateChannelOptions = (channelId: number, options: PlatformOptions, asDefault = false) => {
    setChannelOptions((prev) => ({
      ...prev,
      [channelId]: {
        ...prev[channelId],
        ...options,
      },
    }));
    if (asDefault) {
      setInitialState((prev) => ({
        ...prev,
        channelOptions: {
          ...prev.channelOptions,
          [channelId]: { ...prev.channelOptions[channelId], ...options },
        },
      }));
    }
  };

  /**
   * Set calendar event (note + holiday)
   */
  const setCalendarEvent = (noteId: number | null, holidayId: number | null) => {
    setCalendarNoteId(noteId);
    setCalendarHolidayId(holidayId);
  };

  /**
   * Validation
   */
  const canSubmit = title.trim().length > 0 && selectedChannels.length > 0;
  const canSaveDraft = title.trim().length > 0;

  /** User picked a language (or "none"): from now on the title no longer pre-fills it. */
  const setLanguage = (next: string | null) => {
    languageTouchedRef.current = true;
    setLanguageState(next);
  };

  /**
   * Title typed/picked. This is a PREFILL, not a guess: on a NEW version whose language the user has not chosen
   * yet, a title that names a supported language ("DE", "German version", "FR - quote") pre-fills the Language control, which the
   * user can change. What gets stored is always the explicit control, never the title.
   */
  const changeTitle = (next: string) => {
    setTitle(next);
    if (isEditMode || languageTouchedRef.current) return;
    // "German version", "DE", "FR - quote 02" ... (mirror of the backend resolver); only codes the control offers.
    const code = languageFromTitle(next);
    setLanguageState(code !== null && SUPPORTED_LANGUAGES.some((l) => l.toLowerCase() === code) ? code : null);
  };

  return {
    // Content state
    title,
    setTitle: changeTitle,
    language,
    setLanguage,
    caption,
    setCaption,
    media,
    setMedia,
    addMedia,
    removeMedia,
    reorderMedia,
    setChangedThumbnail,
    labels,
    setLabels,
    mediaMode,
    setMediaMode,
    selectedChannels,
    setChannels: setSelectedChannels,
    scheduledTime,
    setScheduledTime,
    channelOptions,
    updateChannelOptions,
    calendarId,
    setCalendarId,
    calendarNoteId,
    calendarHolidayId,
    setCalendarEvent,
    isCarouselMode,
    setCarouselMode,
    changedThumbnails,

    // Actions
    loadVariation,
    handleSave,
    handleSaveAndMarkReady,
    handleSaveAndSendForReview,
    handleSaveAndRelease,
    handlePrimaryAction,
    handleDiscard,
    reset,

    // Mode
    isEditMode,
    parentPostId,

    // Status
    isSaving: saveMutation.isPending,
    isShooting: shootMutation.isPending,
    isBusy: saveMutation.isPending || shootMutation.isPending,
    isError: saveMutation.isError || shootMutation.isError,
    error: saveMutation.error || shootMutation.error,

    // Validation
    canSubmit,
    canSaveDraft,
    isDirty,
  };
}
